import isEqual from 'lodash/isEqual';
import { DateTime } from 'luxon';
import type {
  ActivationDate,
  DataUsage,
  LastActive,
  SimStatusFilter,
  TargetActivationDate,
} from 'state/SimInventory/SimInventoryStore';
import type { TextFilter } from 'types/common';
import { dateTimeToInputDate } from 'utils/dates';

export interface InputFilters {
  organizationId: string;
  deviceName: string;
  iccid: TextFilter;
  imei: TextFilter;
  activationDate?: ActivationDate;
  targetActivationDate?: TargetActivationDate;
  lastActive?: LastActive;
  dataUsage?: DataUsage;
  simStatuses?: SimStatusFilter;
  tags?: Array<string>;
  noTags?: boolean;
  plans?: Array<string>;
}

const generateDefaultDatePastFilter = () => ({
  timeframe: 'custom',
  start: '',
  end: dateTimeToInputDate(DateTime.now()),
});

const generateDefaultDateFutureFilter = () => ({
  timeframe: 'custom',
  start: dateTimeToInputDate(DateTime.now()),
  end: '',
});

export const defaultFilterValues: InputFilters = {
  organizationId: '',
  deviceName: '',
  iccid: { value: '', condition: 'contains' },
  imei: { value: '', condition: 'is' },
  simStatuses: [],
  tags: [],
  plans: [],
  lastActive: generateDefaultDatePastFilter(),
  activationDate: generateDefaultDatePastFilter(),
  targetActivationDate: generateDefaultDateFutureFilter(),
};

export const filterLabels: { [K in keyof InputFilters]-?: string } = {
  organizationId: 'Organization ID',
  deviceName: 'Name',
  iccid: 'Enabled profile ICCID',
  imei: 'Last known IMEI',
  simStatuses: 'SIM status',
  tags: 'Tags',
  noTags: '', // Not real, no real support for this
  plans: 'Data plan',
  lastActive: 'Last closed session',
  activationDate: 'Activation date',
  targetActivationDate: 'Target activation date',
  dataUsage: 'Data usage',
};

export const getDefaultValues = () => structuredClone(defaultFilterValues);

export const filterSetToDefault = <TKey extends keyof InputFilters>(
  filterName: TKey,
  filter: InputFilters[TKey]
): boolean => !filter || isEqual(filter, defaultFilterValues[filterName]);

export const getDefaultValueForFilter = <TKey extends keyof InputFilters>(
  filterName: TKey
): InputFilters[TKey] => structuredClone(defaultFilterValues[filterName]);
